import { createAsyncThunk } from '@reduxjs/toolkit';
import { SessionReducerActions } from '@common/reducers/DBServiceThunks';
import { backendApiEndpoints } from '@common/reducers/backend-api/backend-api';

// Function to fetch all sessions with pagination
export const fetchAllSessions = createAsyncThunk(
  'sessions/fetchAll',
  async (_, { dispatch }) => {
    // Clear existing sessions first
    dispatch(SessionReducerActions.clearState());
    
    const PAGE_SIZE = 1000;
    let skip = 0;
    let hasMore = true;
    let totalFetched = 0;
    
    try {
      while (hasMore) {
        // Fetch a page of sessions using the API
        const response = await dispatch(
          backendApiEndpoints.getAllSessions.initiate({
            limit: PAGE_SIZE,
            skip,
            sortBy: 'updatedAt',
            sortOrder: -1
          })
        ).unwrap();
        
        // Process the response
        const { total, data } = response;
        
        if (data && data.length > 0) {
          // Add sessions to the Redux store using the session actions
          dispatch(SessionReducerActions.addMany(data));
          totalFetched += data.length;
          
          // Check if we need to continue
          skip += PAGE_SIZE;
          hasMore = skip < total;
        } else {
          hasMore = false;
        }
      }
      
      return { totalFetched };
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }
);

// Utility function to load all sessions
export const loadAllSessions = async (dispatch: any, setIsLoadingSessions: (loading: boolean) => void, 
  setSessionError: (error: Error | null) => void, setPaginationStatus: (status: { total: number }) => void,
  sessions: Record<string, any>) => {
  // Declared outside try so the finally block can always clear it
  let updateInterval: ReturnType<typeof setInterval> | undefined;
  try {
    setIsLoadingSessions(true);
    setSessionError(null);

    // Add a simple progress updater
    updateInterval = setInterval(() => {
      const currentTotal = Object.keys(sessions).length;
      setPaginationStatus({ total: currentTotal });
    }, 500);

    // Fetch all sessions
    const result = await dispatch(fetchAllSessions()).unwrap();
    console.log(`Loaded ${result.totalFetched} total sessions`);

    // Final update
    setPaginationStatus({ total: result.totalFetched });
  } catch (error) {
    console.error('Error loading sessions:', error);
    setSessionError(error instanceof Error ? error : new Error('Failed to load sessions'));
  } finally {
    // Clear the progress updater on both success and error paths
    if (updateInterval !== undefined) clearInterval(updateInterval);
    setIsLoadingSessions(false);
  }
};

// Utility function to toggle node expansion
export const toggleNode = (
  nodeName: string, 
  expandedNodes: Set<string>, 
  setExpandedNodes: (nodes: Set<string>) => void
) => {
  const newExpandedNodes = new Set(expandedNodes);
  if (newExpandedNodes.has(nodeName)) {
    newExpandedNodes.delete(nodeName);
  } else {
    newExpandedNodes.add(nodeName);
  }
  setExpandedNodes(newExpandedNodes);
}; 